import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import SweetAlert from "sweetalert2";
import { connect, useDispatch } from "react-redux";
import Loader from "../../components/LogoLoader";
import {
  getUserInformation,
  resetUserInformation,
} from "../../store/actions/user";
import services from "../../services/";
import { Center } from "../../components/StyledComponents";
import { setPublicKey } from "../../utils/encryption";
import { AUTH_CODES } from "../../constants/authCodes";

/**
 * User inactive tolerance
 * Amount of time in minutes that a user can be inactive
 */
const USER_INACTIVE_TOLERANCE = 100;

const startTimer = (coutdownDate, callback = () => { }, id) => {
  const now = new Date().getTime();
  const distance = coutdownDate - now;

  // const days = Math.floor(distance / (1000 * 60 * 60 * 24));
  // const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  // const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
  // const seconds = Math.floor((distance % (1000 * 60)) / 1000);

  if (distance > 0) {
    //console.log(`${id} Remaining Time: ${days}:${hours}:${minutes}:${seconds}`);
  } else {
    callback();
  }
};

export const AuthContext = React.createContext({
  isAuthenticated: false as boolean,
  passwordExpired: false as boolean,
  isFirstLogin: false as boolean,
  setPasswordExpired: (value: boolean) => { },
});

export const Component = ({
  children,
  signInError,
  signInSuccess,
  signOutSuccess,
}) => {
  const dispatch = useDispatch();
  const [checkingAuth, setCheckingAuth] = useState<boolean>(true);
  const [isInactive, setIsInactive] = useState<boolean>(false);
  const [hasValidToken, setHasValidToken] = useState(false);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [isFirstLogin, setIsFirstLogin] = useState(false);

  const userIntervalRef = useRef<(() => void) | undefined>(undefined);
  const tokenIntervalRef = useRef<(() => void) | undefined>(undefined);

  const fireMessage = () => {
    SweetAlert.fire({
      title: "Hemos notado que te fuiste",
      text: "Por seguridad finalizamos tu sesión.",
      showCancelButton: false,
      confirmButtonText: "Confirmar",
      allowEscapeKey: false,
      preConfirm: () => {
        window.location.assign("/sign-in");
      },
    });
  };

  const whenTokenExpired = (intervalInstance) => {
    clearInterval(intervalInstance);
    if (isInactive) {
      services.auth.signOut();
      fireMessage();
    } else {
      services.auth.refreshToken();
    }
  };

  const whenUserIsInactive = (intervalInstance) => {
    clearInterval(intervalInstance);
    setIsInactive(true);
    services.auth.signOut();
    fireMessage();
  };

  const createInactiveIntervalInstance = () => {
    const fecha = moment().add(USER_INACTIVE_TOLERANCE, "minutes").format();
    const coutdownDate = new Date(fecha).getTime();
    const instance = setInterval(
      () =>
        startTimer(coutdownDate, () => whenUserIsInactive(instance), "User"),
      1000
    );
    return () => {
      clearInterval(instance);
    };
  };

  const createTokenExpirationIntervalInstance = () => {
    const coutdownDate = services.auth.getTokenExpirationTime()?.getTime();
    const instance = setInterval(
      () => startTimer(coutdownDate, () => whenTokenExpired(instance), "Token"),
      1000
    );
    return () => {
      clearInterval(instance);
    };
  };

  const userAuthenticated = useCallback(() => {
    services.auth.userAuthenticated((isValidToken, accessData) => {
      if (isValidToken) {
        setHasValidToken(true);
        dispatch(getUserInformation());

        const passwordExpiredByFlags = Boolean(
          accessData?.passwordExpired ||
          accessData?.password_expired ||
          accessData?.isPasswordExpired ||
          accessData?.passwordChangeRequired ||
          accessData?.password_change_required ||
          accessData?.requiresPasswordChange ||
          accessData?.mustChangePassword
        );

        const passwordExpiredByCode =
          accessData?.code === AUTH_CODES.PASSWORD_EXPIRED;

        const passwordExpiredByMessage =
          accessData?.message === "PASSWORD_CHANGE_REQUIRED";

        if (passwordExpiredByFlags || passwordExpiredByCode || passwordExpiredByMessage) {
          setPasswordExpired(true);
          setIsFirstLogin(!!accessData.isFirstLogin);
        } else {
          setPasswordExpired(false);
          setIsFirstLogin(false);
        }
      } else {
        setHasValidToken(false);
        dispatch(resetUserInformation());
        setPasswordExpired(false);
        setIsFirstLogin(false);
      }

      setCheckingAuth(false);
    });
  }, [dispatch]);

  // createInactiveIntervalInstance and createTokenExpirationIntervalInstance are intentionally
  // excluded from deps: they depend on isInactive state (via whenTokenExpired) which would cause
  // the interval setup to restart on every activity event, breaking the inactivity timer logic.
  useEffect(() => {
    if (hasValidToken) {
      userIntervalRef.current = createInactiveIntervalInstance();
      tokenIntervalRef.current = createTokenExpirationIntervalInstance();
    }

    const handleClick = () => {
      if (typeof userIntervalRef.current === "function") {
        userIntervalRef.current();
        userIntervalRef.current = createInactiveIntervalInstance();
      }

      if (typeof tokenIntervalRef.current === "function") {
        tokenIntervalRef.current();
        tokenIntervalRef.current = createTokenExpirationIntervalInstance();
      }
    };

    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidToken]);

  useEffect(() => {
    userAuthenticated();
  }, [signInSuccess, userAuthenticated]);

  useEffect(() => {
    if (!!signInError) {
      setHasValidToken(false);
    }
  }, [signInError]);

  useEffect(() => {
    userAuthenticated();
  }, [userAuthenticated]);

  useEffect(() => {
    services.auth.getPublicKey().then((resp) => {
      if (resp && resp.key) {
        setPublicKey(resp.key);
      }
    });
  }, []);

  useEffect(() => {
    if (signOutSuccess) {
      window.location.href = "/";
    }
  }, [signOutSuccess]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: hasValidToken,
        passwordExpired,
        isFirstLogin,
        setPasswordExpired,
      }}
    >
      {checkingAuth ? (
        <Center horizontal="center" vertical="center">
          <Loader />
        </Center>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

const state = ({ authStore }) => {
  const {
    loading: signInLoading,
    error: signInError,
    success: signInSuccess,
  } = authStore.signin.states;
  const {
    loading: signOutLoading,
    error: signOutError,
    success: signOutSuccess,
  } = authStore.signout.states;
  return {
    signInLoading,
    signInError,
    signInSuccess,
    signOutLoading,
    signOutError,
    signOutSuccess,
  };
};

export default connect(state)(Component);
